import React, { useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Space,
  DatePicker,
  Select,
  Table,
} from 'antd';
import { Line } from '@ant-design/charts';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/vi_VN';
import {
  dashboardService,
  prescriptionService,
  doctorService,
  type DashboardRange,
} from '../../services';
import { formatCurrency } from '../../utils/format';
import { Prescription } from '../../types';
import PrescriptionDetailModal from '../../components/PrescriptionDetailModal';
import SEO from '../../components/SEO';
import './DashboardPage.css';

const { RangePicker } = DatePicker;

const RANGE_BUTTONS: { key: DashboardRange; label: string }[] = [
  { key: 'today', label: 'Hôm nay' },
  { key: 'week', label: 'Tuần này' },
  { key: 'month', label: 'Tháng này' },
  { key: 'quarter', label: 'Quý này' },
  { key: 'year', label: 'Năm nay' },
  { key: 'custom', label: 'Tùy chọn' },
];

const LIST_OPTIONS = [
  { value: 'prescriptions', label: 'Danh sách đơn thuốc' },
  { value: 'national', label: 'Danh sách đơn thuốc quốc gia' },
];

const DashboardPage: React.FC = () => {
  const [range, setRange] = useState<DashboardRange>('today');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [listType, setListType] = useState<'prescriptions' | 'national'>('prescriptions');
  const [selectedDoctor, setSelectedDoctor] = useState<string | undefined>();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState<number | null>(null);

  const queryParams =
    range === 'custom' && dateRange
      ? {
          range: 'custom' as const,
          from: dateRange[0].format('YYYY-MM-DD'),
          to: dateRange[1].format('YYYY-MM-DD'),
        }
      : { range };

  const dateFilters = useMemo(() => {
    if (range === 'custom' && dateRange) {
      return {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
      };
    }
    const today = dayjs();
    switch (range) {
      case 'today':
        return {
          startDate: today.format('YYYY-MM-DD'),
          endDate: today.format('YYYY-MM-DD'),
        };
      case 'week':
        return {
          startDate: today.startOf('week').format('YYYY-MM-DD'),
          endDate: today.endOf('week').format('YYYY-MM-DD'),
        };
      case 'month':
        return {
          startDate: today.startOf('month').format('YYYY-MM-DD'),
          endDate: today.endOf('month').format('YYYY-MM-DD'),
        };
      case 'quarter': {
        const q = Math.floor(today.month() / 3) + 1;
        const start = today.month((q - 1) * 3).startOf('month');
        const end = today.month((q - 1) * 3 + 2).endOf('month');
        return {
          startDate: start.format('YYYY-MM-DD'),
          endDate: end.format('YYYY-MM-DD'),
        };
      }
      case 'year':
        return {
          startDate: today.startOf('year').format('YYYY-MM-DD'),
          endDate: today.endOf('year').format('YYYY-MM-DD'),
        };
      default:
        return {};
    }
  }, [range, dateRange]);

  const { data: infoResponse, isLoading } = useQuery({
    queryKey: ['dashboard-info', queryParams],
    queryFn: () => dashboardService.getInfoDashboard(queryParams),
    enabled: range !== 'custom' || !!dateRange,
  });

  const { data: doctors } = useQuery({
    queryKey: ['doctors'],
    queryFn: () => doctorService.getList(),
  });

  const { data: prescriptionsResponse, isLoading: prescriptionsLoading } = useQuery({
    queryKey: ['dashboard-prescriptions', listType, dateFilters, selectedDoctor],
    queryFn: () =>
      listType === 'national'
        ? prescriptionService.getNationalPrescriptions({
            ...dateFilters,
            doctorName: selectedDoctor,
            pageSize: 100,
          })
        : prescriptionService.getList({
            ...dateFilters,
            doctorName: selectedDoctor,
            pageSize: 100,
          }),
    enabled: range !== 'custom' || !!dateRange,
  });

  const dashboardData = infoResponse?.data;
  const prescriptions = prescriptionsResponse?.data || [];
  const chartData =
    dashboardData?.chart?.labels?.map((label, i) => ({
      time: label,
      revenue: dashboardData.chart.values[i] ?? 0,
    })) ?? [];

  const chartConfig =
    chartData.length > 0
      ? {
          data: chartData,
          xField: 'time' as const,
          yField: 'revenue' as const,
          smooth: true,
          point: {
            size: 4,
            shape: 'circle' as const,
          },
          color: '#385da4',
          yAxis: {
            label: {
              formatter: (value: number) => `${(value / 1000).toFixed(0)}k`,
            },
          },
        }
      : null;

  const handleRangeChange = (newRange: DashboardRange) => {
    setRange(newRange);
    if (newRange !== 'custom') {
      setDateRange(null);
    }
  };

  const getTotalAmount = (p: Prescription) =>
    parseFloat(p.consultation_fee || '0') + parseFloat(p.medicine_fee || '0');

  const totalAmount = prescriptions.reduce((sum, p) => sum + getTotalAmount(p), 0);
  const totalTax = 0;
  const totalRevenue = totalAmount;

  const columns = [
    { title: 'STT', dataIndex: 'index', key: 'index', width: 60 },
    {
      title: 'Mã đơn',
      dataIndex: 'code',
      key: 'code',
      render: (text: string, record: { key?: string | number; id?: number }) =>
        record.key === 'total' ? (
          text
        ) : (
          <Button
            type="link"
            onClick={() => {
              if (record.id) {
                setSelectedPrescriptionId(record.id);
                setDetailModalVisible(true);
              }
            }}
            className="prescription-code-link"
          >
            {text}
          </Button>
        ),
    },
    { title: 'Tên bác sĩ', dataIndex: 'doctor_name', key: 'doctor_name' },
    { title: 'Tên bệnh nhân', dataIndex: 'patient_name', key: 'patient_name' },
    { title: 'Chẩn đoán', dataIndex: 'diagnosis', key: 'diagnosis' },
    {
      title: 'Tổng tiền',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      align: 'right' as const,
      render: (val: string, record: { key?: string }) =>
        record.key === 'total' ? val : formatCurrency(getTotalAmount(record as Prescription)),
    },
    {
      title: 'Thuế',
      dataIndex: 'tax',
      key: 'tax',
      align: 'right' as const,
      render: (val: string, record: { key?: string }) =>
        record.key === 'total' ? val : '0₫',
    },
    {
      title: 'Tổng doanh thu',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      align: 'right' as const,
      render: (val: string, record: { key?: string }) =>
        record.key === 'total' ? val : formatCurrency(getTotalAmount(record as Prescription)),
    },
  ];

  const tableData = prescriptions.map((p, i) => ({
    ...p,
    index: i + 1,
    key: p.id,
  }));

  const totalRow = {
    index: 'Tổng',
    key: 'total',
    code: '',
    doctor_name: '',
    patient_name: '',
    diagnosis: '',
    totalAmount: formatCurrency(totalAmount),
    tax: formatCurrency(totalTax),
    totalRevenue: formatCurrency(totalRevenue),
  };

  return (
    <>
      <SEO
        title="Dashboard"
        description="Tổng quan hoạt động phòng khám - Thống kê đơn thuốc, doanh thu và các chỉ số quan trọng"
        noIndex={true}
      />
      <div className="dashboard-page">
        <h1 className="page-title">Dashboard</h1>

        <div className="dashboard-filters">
          <Space wrap>
            {RANGE_BUTTONS.map((btn) => (
              <Button
                key={btn.key}
                type={range === btn.key ? 'primary' : 'default'}
                onClick={() => handleRangeChange(btn.key)}
              >
                {btn.label}
              </Button>
            ))}
            {range === 'custom' && (
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                format="DD/MM/YYYY"
                locale={locale}
                placeholder={['Từ ngày', 'Đến ngày']}
              />
            )}
          </Space>
          <Select
            placeholder="Chọn bác sĩ"
            style={{ width: 200 }}
            allowClear
            value={selectedDoctor}
            onChange={setSelectedDoctor}
            options={doctors?.map((d) => ({ value: d.name, label: d.name }))}
          />
        </div>

        <Card className="revenue-card">
          <div className="revenue-summary">
            <span className="revenue-label">Doanh thu:</span>
            <span className="revenue-value">
              {dashboardData ? formatCurrency(dashboardData.total_revenue) : '0₫'}
            </span>
          </div>
        </Card>

        <Row gutter={16} style={{ marginTop: 24 }}>
          <Col span={16}>
            <Card title="Doanh thu" loading={isLoading}>
              {chartConfig && <Line {...chartConfig} height={300} />}
            </Card>
          </Col>
          <Col span={8}>
            <Card title="Dịch vụ" loading={isLoading}>
              <div className="services-list">
                {dashboardData?.services?.map((service) => (
                  <div key={service.code} className="service-item">
                    <div className="service-name">{service.name}</div>
                    <div className="service-details">
                      <span>Số lượng: {service.count}</span>
                      <span className="service-amount">
                        Tổng tiền: {formatCurrency(service.total_amount)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>

        <Card
          title={
            <Space>
              <span>Danh sách</span>
              <Select
                value={listType}
                onChange={setListType}
                style={{ width: 260 }}
                options={LIST_OPTIONS}
              />
            </Space>
          }
          style={{ marginTop: 24 }}
        >
          <Table
            columns={columns as any}
            dataSource={[totalRow, ...tableData] as any}
            loading={prescriptionsLoading}
            pagination={false}
            size="middle"
            summary={() => null}
            rowClassName={(record) =>
              (record as { key?: string }).key === 'total' ? 'dashboard-total-row' : ''
            }
          />
        </Card>

        <PrescriptionDetailModal
          visible={detailModalVisible}
          prescriptionId={selectedPrescriptionId}
          onClose={() => {
            setDetailModalVisible(false);
            setSelectedPrescriptionId(null);
          }}
        />
      </div>
    </>
  );
};

export default DashboardPage;
